import React, { Component } from 'react'
import { connect } from 'react-redux'
import { reduxForm, InjectedFormProps, Field } from 'redux-form'
import injectSheet from 'react-jss'
import { Classes } from 'jss'
import { push, Push } from 'connected-react-router'
import I from 'immutable'
import { sheet } from './sheet'
import { Button } from 'components/ui/Button'
import * as A from 'domain/env'
import { FieldInput } from 'components/Form/Fields/FieldInput'
import Input from 'components/Form/Input'
import { TextButton } from 'components/ui/TextButton'
import { compose } from 'redux'
import { ComposeType } from 'types/common'
import { otpLoginSelector } from 'domain/env'
import BackgroundWithFigures from 'components/common/BackgroundWithFigures'

interface FormValues {
  code: string
}

interface OwnProps {
  classes: Classes
  otpLogin: I.Map<string, any> | null
  signInOtpVerify: (payload: { otpRequestId: number; code: string }) => void
  signInOtpRequest: (payload: { email: string; password: string }) => void
  pushTo: Push
}

type Props = InjectedFormProps<FormValues, OwnProps> & OwnProps

interface State {
  resendIn: number
  expiresIn: number
  lockIn: number
}

class OtpLogIn extends Component<Props, State> {
  private tickTimer?: number

  constructor(props: Props) {
    super(props)

    const { resendIn, expiresIn, lockIn } = this.computeTimersFromProps(props)
    this.state = { resendIn, expiresIn, lockIn }
  }

  componentDidMount() {
    // tslint:disable-next-line:no-shadowed-variable
    const { otpLogin, pushTo } = this.props

    if (!otpLogin || !otpLogin.get('otpRequestId')) {
      pushTo('/log-in')
      return
    }

    this.startTimer()
  }

  componentDidUpdate(prevProps: Props) {
    if (!I.is(prevProps.otpLogin, this.props.otpLogin)) {
      // when new otpRequestId (resend) or lockedUntil changed
      this.startTimer()
    }
  }

  componentWillUnmount() {
    this.clearTimer()
  }

  clearTimer() {
    if (this.tickTimer) {
      window.clearInterval(this.tickTimer)
      this.tickTimer = undefined
    }
  }

  startTimer() {
    this.clearTimer()
    this.setState(this.computeTimersFromProps(this.props)) // calculate once

    // and recalculate every 1 second
    this.tickTimer = window.setInterval(() => {
      this.setState(this.computeTimersFromProps(this.props))
    }, 1000)
  }

  computeTimersFromProps(props: Props): State {
    const { otpLogin } = props

    if (!otpLogin) {
      return { resendIn: 0, expiresIn: 0, lockIn: 0 }
    }

    const now = Date.now()

    const expiresAtStr = otpLogin.get('expiresAt')
    const canResendAtStr = otpLogin.get('canResendAt')
    const lockedUntilStr = otpLogin.get('lockedUntil')

    const expiresAt = expiresAtStr ? new Date(expiresAtStr).getTime() : 0
    const canResendAt = canResendAtStr ? new Date(canResendAtStr).getTime() : 0
    const lockedUntil = lockedUntilStr ? new Date(lockedUntilStr).getTime() : 0

    const expiresIn = expiresAt > 0 ? Math.max(0, Math.floor((expiresAt - now) / 1000)) : 0
    const resendIn = canResendAt > 0 ? Math.max(0, Math.floor((canResendAt - now) / 1000)) : 0
    const lockIn = lockedUntil > 0 ? Math.max(0, Math.floor((lockedUntil - now) / 1000)) : 0

    return { resendIn, expiresIn, lockIn }
  }

  submit = (values: FormValues) => {
    const { otpLogin, signInOtpVerify } = this.props
    if (!otpLogin) return

    const otpRequestId = otpLogin.get('otpRequestId')
    if (!otpRequestId) return
    const code = values.code.trim()

    signInOtpVerify({
      otpRequestId,
      code
    })
  }

  handleResend = () => {
    const { otpLogin, signInOtpRequest } = this.props
    if (!otpLogin) return
    if (this.state.resendIn > 0) return

    const email = otpLogin.get('email')
    const password = otpLogin.get('password')

    if (!email || !password) return

    signInOtpRequest({ email, password })
  }

  goBack = () => {
    this.props.pushTo('/log-in')
  }

  formatMMSS(totalSeconds: number): string {
    const s = Math.max(0, Math.floor(totalSeconds || 0))
    const m = Math.floor(s / 60)
    const r = s % 60
    return `${m}:${String(r).padStart(2, '0')}`
  }

  render() {
    const { classes, handleSubmit, otpLogin } = this.props
    const { resendIn, expiresIn, lockIn } = this.state
    const email = otpLogin && otpLogin.get('email')
    const expiresText = this.formatMMSS(expiresIn)
    const lockText = this.formatMMSS(lockIn)
    const resendText = this.formatMMSS(resendIn)

    const isVerifyDisabled = expiresIn === 0 || lockIn > 0

    return (
      <form onSubmit={handleSubmit(this.submit)}>
        <div className={classes.content}>
          {/* LEFT BRAND PANEL */}
          <div className={`${classes.logoFlexWrapper} d-flex`}>
            <div className={`${classes.logoWrapper} flex-center column flex1`}>
              <BackgroundWithFigures />
              <img className={classes.logo} src="/skilentLogo.png" />
              <div className="text-logo">Your privacy matters</div>
              <div className="text-logo-second">
                We verify it’s you <br />
                before we sign you in.
              </div>
            </div>
          </div>

          {/* RIGHT FORM PANEL */}
          <div className={`${classes.form} flex-center column`}>
            <div className={`${classes.formTitle} flex-center-align column`}>
              <div className="text-login-title">Enter verification code</div>
              <div className="text-login-second">
                {email ? (
                  <>
                    We sent a login code to <b>{email}</b>.
                  </>
                ) : (
                  'We sent a login code to your email.'
                )}
              </div>
            </div>

            <div className={`d-flex column fill ${classes.formInner}`}>
              <FieldInput
                name="code"
                type="text"
                component={Input}
                title="Verification code"
                placeholder="Enter 6-characters code"
                required={true}
                normalize={(value: string) => (value ? value.toUpperCase() : value)}
              />

              <div className={`${classes.buttonGroup} d-flex column fill`}>
                <div className={classes.statusLine}>
                  {lockIn > 0 ? (
                    <span className="text-warning">
                      Try again in <b>{lockText}</b>.
                    </span>
                  ) : expiresIn > 0 ? (
                    <span className="text-sub">
                      Code expires in <b>{expiresText}</b>.
                    </span>
                  ) : (
                    <span className="text-danger">Code expired. Request a new one.</span>
                  )}
                </div>

                <Button
                  size="md"
                  fill={true}
                  label="Verify"
                  type="submit"
                  disabled={isVerifyDisabled}
                />

                <Button
                  size="md"
                  type="button"
                  label={resendIn > 0 ? `Resend (${resendText})` : 'Resend code'}
                  disabled={resendIn > 0}
                  fill={true}
                  onClick={this.handleResend}
                />

                <div className={'text-12 text-regular flex-center-justify'}>
                  <span>
                    Wrong email?{' '}
                    <TextButton onClick={this.goBack} className="text-12 text-semibold">
                      Back to login.
                    </TextButton>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    )
  }
}

const validate = (values: FormValues) => {
  const errors: Partial<FormValues> = {}

  const code = values && values.code && values.code.trim()

  if (!code) {
    errors.code = 'Code is required'
  } else if (!/^[A-Z0-9]{6}$/i.test(code)) {
    errors.code = 'Invalid code format'
  }

  return errors
}

export default compose<ComposeType<Props>>(
  connect(
    (state: any) => ({
      otpLogin: otpLoginSelector(state)
    }),
    {
      signInOtpVerify: A.signInOtpVerify,
      signInOtpRequest: A.signInOtpRequest,
      pushTo: push
    }
  ),
  reduxForm<FormValues, OwnProps>({
    form: 'log-in-otp',
    validate
  }),
  injectSheet(sheet)
)(OtpLogIn)
